/**
 * Lanificio — collectie- en zoekresultatengrid.
 *
 * Doet vier dingen, allemaal zonder paginaverversing:
 *  1. kolommenschakelaar (2/3/4), bewaard in localStorage, met de sizes
 *     van de kaartfoto's mee;
 *  2. filter- en sorteerpanelen openen/sluiten (op mobiel: één drawer);
 *  3. filteren/sorteren via de Section Rendering API, met de filters in de
 *     URL zodat een gefilterde pagina te delen is;
 *  4. "Meer kleden laden": de volgende pagina ophalen en eronder plakken.
 *
 * Alle events lopen via delegatie op de root, zodat het vervangen van de
 * resultaten geen listeners hoeft te herbinden.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'lanificio:kolommen';

  /**
   * localStorage kan in een privevenster gooien; nooit de hele component
   * laten omvallen over een bewaarde voorkeur.
   */
  function readStoredColumns() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function storeColumns(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (error) {
      /* voorkeur gaat verloren, verder niets aan de hand */
    }
  }

  /**
   * sizes voor de kaartfoto's bij een gekozen aantal kolommen. Volgt het
   * grid: marge 20px (mobiel) / 40px, gap 16 / 24 / 32px, grid max 1440px.
   * Mobiel toont hooguit 2 kolommen, tablet altijd 2. Zonder keuze geeft
   * de server al de juiste sizes mee (1 / 2 / 3 kolommen).
   */
  function cardSizes(columns) {
    var desktop = parseInt(columns, 10) || 3;
    var mobile = Math.min(desktop, 2);
    var desktopGaps = (desktop - 1) * 32;
    var maxWidth = Math.floor((1440 - desktopGaps) / desktop);
    var mobileWidth =
      mobile === 1 ? 'calc(100vw - 40px)' : 'calc((100vw - ' + (40 + 16) + 'px) / 2)';

    return [
      '(min-width: 1520px) ' + maxWidth + 'px',
      '(min-width: 990px) calc((100vw - ' + (80 + desktopGaps) + 'px) / ' + desktop + ')',
      '(min-width: 750px) calc(50vw - 52px)',
      mobileWidth,
    ].join(', ');
  }

  function LanCollection(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.abortController = null;
    this.bind();
    this.applyStoredColumns();
    this.warnIfFiltersMissing();
  }

  // Filters horen er te staan maar collection.filters is leeg: dan staat er
  // in Search & Discovery niets aan (of niets dat voor deze collectie geldt).
  LanCollection.prototype.warnIfFiltersMissing = function () {
    if (!this.root.querySelector('[data-lan-filters-empty]')) return;
    console.warn(
      '[Lanificio] collection.filters is leeg: alleen Sorteren wordt getoond. ' +
        'Zet Prijs, Kleur en Maat aan in Search & Discovery → Filters.'
    );
  };

  LanCollection.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      var colsButton = target.closest('[data-lan-cols] button');
      if (colsButton) {
        self.setColumns(colsButton.getAttribute('data-cols'), true);
        return;
      }

      var toggle = target.closest('[data-lan-toggle]');
      if (toggle) {
        self.togglePanel(toggle);
        return;
      }

      var drawerOpen = target.closest('[data-lan-drawer-open]');
      if (drawerOpen) {
        self.setDrawer(true);
        // "Sorteren" (mobiel) springt in de drawer naar de sorteeropties.
        if (drawerOpen.getAttribute('data-lan-drawer-focus') === 'sort') {
          var sortGroup = self.root.querySelector('[data-lan-sort-group]');
          if (sortGroup) sortGroup.scrollIntoView({ block: 'nearest' });
        }
        return;
      }

      if (target.closest('[data-lan-drawer-close]')) {
        self.setDrawer(false);
        return;
      }

      if (target.closest('[data-lan-price-apply]')) {
        self.submitForm();
        return;
      }

      var loadMore = target.closest('[data-lan-load-more]');
      if (loadMore) {
        event.preventDefault();
        self.loadMore(loadMore);
        return;
      }

      // Filterchips en "Alles wissen" zijn gewone links naar een gefilterde
      // URL — onderschept, zodat ook die zonder verversing gaan.
      var filterLink = target.closest('[data-lan-filter-link]');
      if (filterLink) {
        event.preventDefault();
        self.navigate(filterLink.getAttribute('href'));
      }
    });

    this.root.addEventListener('change', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-lan-form]')) return;
      // Prijs gaat pas mee via "Toepassen" (of Enter), niet bij elke stap.
      if (target.closest('[data-lan-price]')) return;

      self.submitForm();

      // Zonder filters staat er alleen Sorteren in de drawer: na een keuze
      // valt er niets meer te doen, dus dicht.
      if (target.name === 'sort_by' && self.root.querySelector('[data-lan-sort-only]')) {
        self.setDrawer(false);
      }
    });

    // Enter in een prijsveld verstuurt het formulier: zelf afhandelen.
    this.root.addEventListener('submit', function (event) {
      if (!(event.target instanceof Element) || !event.target.matches('[data-lan-form]')) return;
      event.preventDefault();
      self.submitForm();
    });

    // Terug/vooruit in de browser: de resultaten van die URL opnieuw ophalen.
    window.addEventListener('popstate', function () {
      self.navigate(window.location.href, false);
    });

    // De twee prijsvelden en hun sliders in de pas houden tijdens het slepen.
    this.root.addEventListener('input', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-lan-price]')) return;

      self.syncPriceInputs(target);
    });

    // Panelen sluiten bij een klik buiten de filterbalk.
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (self.root.contains(target)) return;

      self.closeAllPanels();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      self.closeAllPanels();
      self.setDrawer(false);
    });
  };

  // --------------------------------------------------------------- kolommen
  LanCollection.prototype.applyStoredColumns = function () {
    var stored = readStoredColumns();
    if (stored) this.setColumns(stored, false);
  };

  LanCollection.prototype.setColumns = function (value, persist) {
    if (!value) return;

    this.columns = value;
    var grid = this.root.querySelector('[data-lan-grid]');
    if (grid) {
      grid.style.setProperty('--lan-columns', value);
      this.applyCardSizes(grid);
    }

    var buttons = this.root.querySelectorAll('[data-lan-cols] button');
    Array.prototype.forEach.call(buttons, function (button) {
      var active = button.getAttribute('data-cols') === value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (persist) storeColumns(value);
  };

  // Alleen als er een kolomkeuze is; anders klopt de sizes van de server.
  LanCollection.prototype.applyCardSizes = function (scope) {
    if (!this.columns) return;
    var sizes = cardSizes(this.columns);
    var images = scope.querySelectorAll('.lan-card__image');
    Array.prototype.forEach.call(images, function (image) {
      if (image.getAttribute('sizes') !== sizes) image.setAttribute('sizes', sizes);
    });
  };

  // ---------------------------------------------------------------- panelen
  LanCollection.prototype.togglePanel = function (toggle) {
    var group = toggle.closest('[data-lan-group]');
    if (!group) return;

    var panel = group.querySelector('[data-lan-panel]');
    if (!panel) return;

    var willOpen = !panel.classList.contains('is-open');
    this.closeAllPanels();

    if (willOpen) {
      panel.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    }
  };

  LanCollection.prototype.closeAllPanels = function () {
    var panels = this.root.querySelectorAll('[data-lan-panel].is-open');
    Array.prototype.forEach.call(panels, function (panel) {
      panel.classList.remove('is-open');
      var group = panel.closest('[data-lan-group]');
      var toggle = group && group.querySelector('[data-lan-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  };

  LanCollection.prototype.openPanelIndexes = function () {
    var groups = Array.prototype.slice.call(this.root.querySelectorAll('[data-lan-group]'));
    var open = [];

    groups.forEach(function (group, index) {
      if (group.querySelector('[data-lan-panel].is-open')) open.push(index);
    });

    return open;
  };

  LanCollection.prototype.openPanelsByIndex = function (indexes) {
    var groups = this.root.querySelectorAll('[data-lan-group]');

    indexes.forEach(function (index) {
      var group = groups[index];
      if (!group) return;

      var panel = group.querySelector('[data-lan-panel]');
      var toggle = group.querySelector('[data-lan-toggle]');

      if (panel) panel.classList.add('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    });
  };

  LanCollection.prototype.setDrawer = function (open) {
    this.root.classList.toggle('lan-col--drawer-open', open);
    document.documentElement.classList.toggle('lan-drawer-lock', open);
  };

  // ------------------------------------------------------------------ prijs
  LanCollection.prototype.syncPriceInputs = function (target) {
    var wrapper = target.closest('[data-lan-price]');
    if (!wrapper) return;

    var role = target.getAttribute('data-lan-price-role');
    if (!role) return;

    var isRange = target.getAttribute('type') === 'range';
    var slider = wrapper.querySelector('input[type="range"][data-lan-price-role="' + role + '"]');
    var field = wrapper.querySelector('input[type="number"][data-lan-price-role="' + role + '"]');

    if (isRange && field) field.value = target.value;
    if (!isRange && slider) slider.value = target.value;

    var min = wrapper.querySelector('input[type="number"][data-lan-price-role="gte"]');
    var max = wrapper.querySelector('input[type="number"][data-lan-price-role="lte"]');

    if (min && max && min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)) {
      if (role === 'gte') max.value = min.value;
      else min.value = max.value;
    }
  };

  // -------------------------------------------------------------- filteren
  LanCollection.prototype.submitForm = function () {
    var form = this.root.querySelector('[data-lan-form]');
    if (!form) return;

    var params = new URLSearchParams();

    Array.prototype.forEach.call(form.elements, function (element) {
      if (!element.name || element.disabled) return;
      // De sliders dragen dezelfde naam als de getalvelden; anders zou elke
      // prijsgrens dubbel in de URL komen.
      if (element.getAttribute('type') === 'range') return;
      if ((element.type === 'checkbox' || element.type === 'radio') && !element.checked) return;
      if (element.value === '') return;

      params.append(element.name, element.value);
    });

    var action = form.getAttribute('action') || window.location.pathname;
    var query = params.toString();

    this.navigate(query ? action + '?' + query : action);
  };

  LanCollection.prototype.navigate = function (url, push) {
    if (!url) return;
    if (push === undefined) push = true;

    var self = this;
    var open = this.openPanelIndexes();
    var target = new URL(url, window.location.origin);
    var fetchUrl = new URL(target.toString());

    fetchUrl.searchParams.set('section_id', this.sectionId);

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.root.classList.add('lan-col--busy');

    fetch(fetchUrl.toString(), { signal: this.abortController.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Kon de resultaten niet laden');
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-lan-root]');
        if (!fresh) throw new Error('Geen resultaten in het antwoord');

        self.replaceRegion('[data-lan-facets]', fresh);
        self.replaceRegion('[data-lan-results]', fresh);

        if (push) window.history.pushState({}, '', target.toString());

        self.applyStoredColumns();
        self.openPanelsByIndex(open);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        // Liever een gewone navigatie dan een stille mislukking.
        window.location.assign(target.toString());
      })
      .finally(function () {
        self.root.classList.remove('lan-col--busy');
      });
  };

  LanCollection.prototype.replaceRegion = function (selector, fresh) {
    var current = this.root.querySelector(selector);
    var replacement = fresh.querySelector(selector);

    if (!current || !replacement) return;

    current.innerHTML = replacement.innerHTML;
  };

  // ------------------------------------------------------------ meer laden
  LanCollection.prototype.loadMore = function (button) {
    var self = this;
    var nextUrl = button.getAttribute('data-next-url');

    if (!nextUrl || button.hasAttribute('data-loading')) return;

    var fetchUrl = new URL(nextUrl, window.location.origin);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    button.setAttribute('data-loading', '');
    button.classList.add('is-loading');

    fetch(fetchUrl.toString())
      .then(function (response) {
        if (!response.ok) throw new Error('Kon de volgende pagina niet laden');
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var grid = self.root.querySelector('[data-lan-grid]');
        var freshItems = parsed.querySelectorAll('[data-lan-grid] > li');

        if (grid) {
          Array.prototype.forEach.call(freshItems, function (item) {
            var node = document.importNode(item, true);
            // sizes zetten vóór het invoegen, zodat de juiste maat laadt.
            self.applyCardSizes(node);
            grid.appendChild(node);
          });
        }

        var freshButton = parsed.querySelector('[data-lan-load-more]');
        var freshNext = freshButton && freshButton.getAttribute('data-next-url');

        if (freshNext) {
          button.setAttribute('data-next-url', freshNext);
        } else {
          var wrapper = button.closest('[data-lan-load-more-wrapper]') || button;
          wrapper.remove();
        }
      })
      .catch(function () {
        window.location.assign(fetchUrl.toString());
      })
      .finally(function () {
        button.removeAttribute('data-loading');
        button.classList.remove('is-loading');
      });
  };

  function init(context) {
    var roots = (context || document).querySelectorAll('[data-lan-root]');

    Array.prototype.forEach.call(roots, function (root) {
      if (root.hasAttribute('data-lan-ready')) return;
      root.setAttribute('data-lan-ready', '');
      new LanCollection(root);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });

  // De theme-editor plaatst secties opnieuw in de DOM.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
